import axios from 'axios';
import { Agent, AgentFormData } from '../types/index';
import { mapAutonomyLevel } from '../utils/format';

interface PaginationData {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

interface ApiResponse {
    agents: any[];
    pagination: PaginationData;
}

const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    }
});

export const fetchAgents = async (page: number = 1): Promise<{ agents: Agent[], pagination: PaginationData }> => {
    try {
        const { data } = await api.get<ApiResponse>(`/agents?page=${page}`);
        
        const mappedAgents = data.agents.map((agent: any) => {
            // Parse contract addresses to extract chains
            const chains = agent.contract_addresses?.map((contractStr: string) => {
                try {
                    const contractData = JSON.parse(contractStr);
                    return contractData.chain;
                } catch (e) {
                    console.error('Error parsing contract address:', e);
                    return null;
                }
            }).filter(Boolean) || [];

            return {
                id: agent.id || Date.now(),
                name: agent.name || '',
                icon: agent.icon || '',
                description: agent.description || '',
                marketCap: agent.market_cap || '0',
                level: mapAutonomyLevel(agent.level_of_autonomy),
                token_utility: agent.token_utility || '1',
                openSource: agent.open_source_status === 'FULLY_OPEN',
                framework: Array.isArray(agent.frameworks) ? agent.frameworks[0] : '',
                tags: Array.isArray(agent.tags) ? agent.tags : [],
                chains: chains, // Use the parsed chains instead of agent.chains
                contract_addresses: Array.isArray(agent.contract_addresses) ? agent.contract_addresses : [],
                website_url: agent.website_url || '',
                github_url: agent.github_url || '',
                documentation_url: agent.documentation_url || '',
                x_url: agent.x_url || '',
                discord_url: agent.discord_url || '',
                telegram_url: agent.telegram_url || '',
                cookie_url: agent.cookie_url || '',
                agent_team_status: agent.agent_team_status || 'ANON',
                agent_symbol: agent.agent_symbol || '',
                team_anonymous: agent.team_anonymous || false,
                is_creator: agent.is_creator || false,
                status: agent.status || 'PENDING',
                icon_url: agent.icon_url || '',
                bullet_points: agent.bullet_points || []
            };
        });

        return {
            agents: mappedAgents,
            pagination: data.pagination
        };
    } catch (error) {
        console.error('Error fetching agents:', error);
        throw error;
    }
};


export const fetchAgentGithubData = async (agentId: string) => {
    console.log('🔍 Fetching GitHub data for agent ID:', agentId);
    try {
        // console.log(`📡 Making request to: /agents/${agentId}/github`);
        const { data } = await api.get(`/agents/${agentId}/github`);
        // console.log('📥 Received GitHub data:', data);
        return data;
    } catch (error) {
        // console.error('❌ Error fetching GitHub data:', {
        //     agentId,
        //     error: error instanceof Error ? error.message : 'Unknown error',
        //     status: axios.isAxiosError(error) ? error.response?.status : 'N/A',
        //     data: axios.isAxiosError(error) ? error.response?.data : 'N/A'
        // });
        throw error;
    }
};
export const createAgent = async (data: AgentFormData): Promise<Agent> => {
    try {
        console.log('Creating agent with data:', data);

        const transformedData = {
            ...data,
            level_of_autonomy: data.level_of_autonomy,
            token_utility: data.token_utility,
        };

        const response = await api.post('/agents', transformedData);
        
        return {
            id: response.data.id,
            name: response.data.name,
            icon: response.data.icon || '',
            description: response.data.description,
            marketCap: response.data.market_cap,
            level: mapAutonomyLevel(response.data.level_of_autonomy),
            token_utility: response.data.token_utility,
            openSource: response.data.open_source_status === 'FULLY_OPEN',
            framework: Array.isArray(response.data.frameworks) ? response.data.frameworks[0] : '',
            tags: Array.isArray(response.data.tags) ? response.data.tags : [],
            chains: Array.isArray(response.data.chains) ? response.data.chains : [],
            website_url: response.data.website_url,
            github_url: response.data.github_url,
            documentation_url: response.data.documentation_url,
            x_url: response.data.x_url,
            discord_url: response.data.discord_url,
            bullet_points: response.data.bullet_points
        };
    } catch (error) {
        if (axios.isAxiosError(error)) {
            console.error('Error response:', error.response?.data);
            throw new Error(error.response?.data?.error || error.message);
        }
        throw error;
    }
};

// Add error interceptor
api.interceptors.response.use(
    response => response,
    error => {
        if (axios.isAxiosError(error)) {
            console.error('API Error:', {
                status: error.response?.status,
                data: error.response?.data,
                message: error.message
            });
        }
        return Promise.reject(error);
    }
); 